/**
 * Toolbar Component
 * Top toolbar with connection controls and settings
 */

import { AgentInfo, ConnectionStatus, ViewerSettings, VideoStats, WebRTCStats } from '../types';

export interface ToolbarConfig {
    container: HTMLElement;
}

export interface ToolbarEvents {
    onConnect?: () => void;
    onDisconnect?: () => void;
    onAgentSelect?: (agentId: string) => void;
    onMonitorSelect?: (index: number) => void;
    onSettingsChange?: (settings: Partial<ViewerSettings>) => void;
    onFullscreen?: () => void;
    onPointerLock?: () => void;
    onAnalyzeScreen?: () => void;
    onToggleAI?: () => void;
    onFileTransferToggle?: () => void;
}

export class Toolbar {
    private container: HTMLElement;
    private events: ToolbarEvents;
    private agents: AgentInfo[] = [];
    private selectedAgentId: string | null = null;
    private connectionStatus: ConnectionStatus = ConnectionStatus.Disconnected;
    private isP2P = false;
    private videoStats: VideoStats | null = null;
    private networkStats: WebRTCStats | null = null;

    constructor(config: ToolbarConfig, events: ToolbarEvents = {}) {
        this.container = config.container;
        this.events = events;
        this.render();
    }

    /**
     * Update agent list
     */
    updateAgents(agents: AgentInfo[]): void {
        this.agents = agents;
        this.updateAgentSelect();
    }

    /**
     * Update connection status
     */
    updateStatus(status: ConnectionStatus, isP2P: boolean = false): void {
        this.connectionStatus = status;
        this.isP2P = isP2P;
        this.updateStatusDisplay();
    }

    /**
     * Update video statistics
     */
    updateVideoStats(stats: VideoStats): void {
        this.videoStats = stats;
        this.updateStatsDisplay();
    }

    /**
     * Update network statistics
     */
    updateNetworkStats(stats: WebRTCStats): void {
        this.networkStats = stats;
        this.updateStatsDisplay();
    }

    /**
     * Show/hide toolbar
     */
    setVisible(visible: boolean): void {
        this.container.style.display = visible ? 'flex' : 'none';
    }

    // =========================================================================
    // Private Methods
    // =========================================================================

    private render(): void {
        this.container.innerHTML = `
            <div class="toolbar">
                <div class="toolbar-section toolbar-left">
                    <div class="toolbar-logo">
                        <span class="logo-icon">🖥️</span>
                        <span class="logo-text">LEVOR</span>
                    </div>
                </div>

                <div class="toolbar-section toolbar-center">
                    <select id="agent-select" class="toolbar-select" disabled>
                        <option value="">בחר מחשב...</option>
                    </select>

                    <select id="monitor-select" class="toolbar-select" disabled>
                        <option value="0">מסך ראשי</option>
                    </select>

                    <button id="connect-btn" class="toolbar-btn btn-primary" disabled>
                        <span class="btn-icon">🔗</span>
                        <span class="btn-text">התחבר</span>
                    </button>

                    <button id="disconnect-btn" class="toolbar-btn btn-danger" style="display: none;">
                        <span class="btn-icon">⛔</span>
                        <span class="btn-text">נתק</span>
                    </button>
                </div>

                <div class="toolbar-section toolbar-right">
                    <div class="status-indicator" id="status-indicator">
                        <span class="status-dot"></span>
                        <span class="status-text">מנותק</span>
                    </div>

                    <div class="quality-indicator" id="quality-indicator" title="איכות חיבור">
                        <span class="qi-bars" id="qi-bars">
                            <span class="qi-bar"></span>
                            <span class="qi-bar"></span>
                            <span class="qi-bar"></span>
                            <span class="qi-bar"></span>
                        </span>
                        <span class="qi-latency" id="qi-latency"></span>
                    </div>
                    <div class="stats-display" id="stats-display">
                        <span class="stat-item" id="stat-fps">-- FPS</span>
                        <span class="stat-item" id="stat-latency">-- ms</span>
                        <span class="stat-item" id="stat-bitrate">-- Mbps</span>
                    </div>

                    <div class="toolbar-actions">
                        <button id="analyze-screen-btn" class="toolbar-btn btn-icon-only" title="ניתוח מסך AI" style="display: none;">
                            <span class="btn-icon">&#x1F50D;</span>
                        </button>

                        <button id="ai-toggle-btn" class="toolbar-btn btn-icon-only" title="עוזר AI (F9)">
                            <span class="btn-icon">&#x1F916;</span>
                        </button>

                        <button id="file-transfer-btn" class="toolbar-btn btn-icon-only" title="העברת קבצים" disabled>
                            <span class="btn-icon">&#x1F4C1;</span>
                        </button>

                        <button id="fullscreen-btn" class="toolbar-btn btn-icon-only" title="מסך מלא">
                            <span class="btn-icon">⛶</span>
                        </button>

                        <button id="settings-btn" class="toolbar-btn btn-icon-only" title="הגדרות">
                            <span class="btn-icon">⚙️</span>
                        </button>
                    </div>
                </div>
            </div>
        `;

        this.attachEventListeners();
    }

    private attachEventListeners(): void {
        // Agent select
        const agentSelect = this.container.querySelector('#agent-select') as HTMLSelectElement;
        agentSelect.addEventListener('change', () => {
            this.selectedAgentId = agentSelect.value || null;
            this.updateMonitorSelect();
            this.updateConnectButton();
        });

        // Monitor select
        const monitorSelect = this.container.querySelector('#monitor-select') as HTMLSelectElement;
        monitorSelect.addEventListener('change', () => {
            const index = parseInt(monitorSelect.value, 10);
            this.events.onMonitorSelect?.(index);
        });

        // Connect button
        const connectBtn = this.container.querySelector('#connect-btn') as HTMLButtonElement;
        connectBtn.addEventListener('click', () => {
            if (this.selectedAgentId) {
                this.events.onAgentSelect?.(this.selectedAgentId);
            }
        });

        // Disconnect button
        const disconnectBtn = this.container.querySelector('#disconnect-btn') as HTMLButtonElement;
        disconnectBtn.addEventListener('click', () => {
            this.events.onDisconnect?.();
        });

        // S2-5: File Transfer button
        const fileTransferBtn = this.container.querySelector('#file-transfer-btn') as HTMLButtonElement;
        fileTransferBtn.addEventListener('click', () => {
            this.events.onFileTransferToggle?.();
        });

        // Fullscreen button
        const fullscreenBtn = this.container.querySelector('#fullscreen-btn') as HTMLButtonElement;
        fullscreenBtn.addEventListener('click', () => {
            this.events.onFullscreen?.();
        });

        // Settings button
        const settingsBtn = this.container.querySelector('#settings-btn') as HTMLButtonElement;
        settingsBtn.addEventListener('click', () => {
            this.showSettingsPanel();
        });

        // Analyze Screen button
        const analyzeBtn = this.container.querySelector('#analyze-screen-btn') as HTMLButtonElement;
        analyzeBtn.addEventListener('click', () => {
            this.events.onAnalyzeScreen?.();
        });

        // AI Toggle button
        const aiToggleBtn = this.container.querySelector('#ai-toggle-btn') as HTMLButtonElement;
        aiToggleBtn.addEventListener('click', () => {
            this.events.onToggleAI?.();
        });
    }

    private updateAgentSelect(): void {
        const select = this.container.querySelector('#agent-select') as HTMLSelectElement;

        // Keep current selection if possible
        const currentValue = select.value;

        select.innerHTML = '<option value="">בחר מחשב...</option>';

        for (const agent of this.agents) {
            const option = document.createElement('option');
            option.value = agent.agentId;
            const modeBadge = agent.requiresApproval ? '🛡️' : '🔓';
            option.textContent = `${modeBadge} ${agent.machineName} (${agent.username})`;

            if (!agent.isAvailable) {
                option.disabled = true;
                option.textContent += ' - תפוס';
            }

            select.appendChild(option);
        }

        // Restore selection if still valid
        if (currentValue && this.agents.some(a => a.agentId === currentValue)) {
            select.value = currentValue;
        }

        select.disabled = this.connectionStatus !== ConnectionStatus.Connected;
        this.updateConnectButton();
    }

    private updateMonitorSelect(): void {
        const select = this.container.querySelector('#monitor-select') as HTMLSelectElement;
        const agent = this.agents.find(a => a.agentId === this.selectedAgentId);

        select.innerHTML = '';

        if (agent?.monitors && agent.monitors.length > 0) {
            for (const monitor of agent.monitors) {
                const option = document.createElement('option');
                option.value = monitor.index.toString();
                const dpi = monitor.refreshRate > 0 ? ` ${monitor.refreshRate}Hz` : '';
                option.textContent = `${monitor.friendlyName || `מסך ${monitor.index + 1}`} (${monitor.width}x${monitor.height}${dpi})`;
                if (monitor.isPrimary) {
                    option.textContent += ' - ראשי';
                }
                option.title = `${monitor.deviceName} | מיקום: ${monitor.left},${monitor.top}`;
                select.appendChild(option);
            }

            // Add "All Monitors" option when there are multiple monitors
            if (agent.monitors.length > 1) {
                const allOption = document.createElement('option');
                allOption.value = '-2'; // MonitorSelection.AllMonitors constant
                allOption.textContent = 'כל המסכים';
                select.appendChild(allOption);
            }

            select.disabled = false;
        } else {
            select.innerHTML = '<option value="0">מסך ראשי</option>';
            select.disabled = true;
        }
    }

    /** Show brief loading state on monitor select during switch */
    public setMonitorSwitching(switching: boolean): void {
        const select = this.container.querySelector('#monitor-select') as HTMLSelectElement;
        if (switching) {
            select.disabled = true;
            select.style.opacity = '0.6';
        } else {
            select.disabled = false;
            select.style.opacity = '1';
        }
    }

    private updateConnectButton(): void {
        const connectBtn = this.container.querySelector('#connect-btn') as HTMLButtonElement;
        connectBtn.disabled = !this.selectedAgentId || this.connectionStatus !== ConnectionStatus.Connected;
    }

    private updateStatusDisplay(): void {
        const indicator = this.container.querySelector('#status-indicator') as HTMLElement;
        const dot = indicator.querySelector('.status-dot') as HTMLElement;
        const text = indicator.querySelector('.status-text') as HTMLElement;

        const connectBtn = this.container.querySelector('#connect-btn') as HTMLButtonElement;
        const disconnectBtn = this.container.querySelector('#disconnect-btn') as HTMLButtonElement;
        const agentSelect = this.container.querySelector('#agent-select') as HTMLSelectElement;

        // Show/hide analyze screen button based on streaming status
        const analyzeScreenBtn = this.container.querySelector('#analyze-screen-btn') as HTMLButtonElement;
        if (analyzeScreenBtn) {
            analyzeScreenBtn.style.display = this.connectionStatus === ConnectionStatus.Streaming ? 'flex' : 'none';
        }

        // S2-5: Enable/disable file transfer button based on streaming
        const fileTransferBtn = this.container.querySelector('#file-transfer-btn') as HTMLButtonElement;
        if (fileTransferBtn) {
            fileTransferBtn.disabled = this.connectionStatus !== ConnectionStatus.Streaming;
        }

        // Remove all status classes
        dot.classList.remove('status-disconnected', 'status-connecting', 'status-connected', 'status-streaming', 'status-error');

        switch (this.connectionStatus) {
            case ConnectionStatus.Disconnected:
                dot.classList.add('status-disconnected');
                text.textContent = 'מנותק';
                connectBtn.style.display = 'flex';
                disconnectBtn.style.display = 'none';
                agentSelect.disabled = true;
                break;

            case ConnectionStatus.Connecting:
                dot.classList.add('status-connecting');
                text.textContent = 'מתחבר...';
                connectBtn.style.display = 'flex';
                connectBtn.disabled = true;
                disconnectBtn.style.display = 'none';
                break;

            case ConnectionStatus.Connected:
                dot.classList.add('status-connected');
                text.textContent = 'מחובר לשרת';
                connectBtn.style.display = 'flex';
                disconnectBtn.style.display = 'none';
                agentSelect.disabled = false;
                this.updateConnectButton();
                break;

            case ConnectionStatus.Streaming:
                dot.classList.add('status-streaming');
                text.textContent = this.isP2P ? 'P2P - זמן אמת' : 'סטרימינג';
                connectBtn.style.display = 'none';
                disconnectBtn.style.display = 'flex';
                agentSelect.disabled = true;
                break;

            case ConnectionStatus.Reconnecting:
                dot.classList.add('status-connecting');
                text.textContent = 'מתחבר מחדש...';
                break;

            case ConnectionStatus.Error:
                dot.classList.add('status-error');
                text.textContent = 'שגיאה';
                connectBtn.style.display = 'flex';
                connectBtn.disabled = false;
                disconnectBtn.style.display = 'none';
                break;
        }
    }

    private updateStatsDisplay(): void {
        const fpsEl = this.container.querySelector('#stat-fps') as HTMLElement;
        const latencyEl = this.container.querySelector('#stat-latency') as HTMLElement;
        const bitrateEl = this.container.querySelector('#stat-bitrate') as HTMLElement;

        if (this.videoStats) {
            fpsEl.textContent = `${this.videoStats.fps} FPS`;
            bitrateEl.textContent = `${(this.videoStats.bitrate / 1_000_000).toFixed(1)} Mbps`;
        }

        if (this.networkStats) {
            latencyEl.textContent = `${Math.round(this.networkStats.roundTripTimeMs)} ms`;
        }

        // WC-14: Update connection quality indicator
        this.updateQualityIndicator();
    }

    private updateQualityIndicator(): void {
        const bars = this.container.querySelectorAll('#qi-bars .qi-bar');
        const latencyEl = this.container.querySelector('#qi-latency') as HTMLElement;
        const indicator = this.container.querySelector('#quality-indicator') as HTMLElement;

        if (!indicator) return;

        // Only show during streaming
        indicator.style.display = this.connectionStatus === ConnectionStatus.Streaming ? 'flex' : 'none';

        const rtt = this.networkStats?.roundTripTimeMs ?? 0;
        const fps = this.videoStats?.fps ?? 0;

        // Determine quality level (0-4 bars)
        let level = 0;
        if (fps > 0) {
            if (rtt < 50 && fps >= 25) level = 4;
            else if (rtt < 100 && fps >= 15) level = 3;
            else if (rtt < 200 && fps >= 10) level = 2;
            else level = 1;
        }

        // Color based on quality
        const colors = ['#FF4444', '#FF8800', '#FFCC00', '#44CC44', '#00DD00'];
        bars.forEach((bar, i) => {
            (bar as HTMLElement).style.opacity = i < level ? '1' : '0.2';
            (bar as HTMLElement).style.backgroundColor = colors[level];
        });

        if (latencyEl && rtt > 0) {
            latencyEl.textContent = `${Math.round(rtt)}ms`;
            latencyEl.style.color = colors[level];
        }
    }

    private settingsPanelVisible = false;

    private showSettingsPanel(): void {
        if (this.settingsPanelVisible) {
            this.hideSettingsPanel();
            return;
        }

        this.settingsPanelVisible = true;
        const existing = this.container.querySelector('.settings-panel');
        if (existing) existing.remove();

        const panel = document.createElement('div');
        panel.className = 'settings-panel';
        panel.innerHTML = `
            <div class="settings-panel-header">
                <span>הגדרות</span>
                <button class="settings-close-btn" title="סגור">✕</button>
            </div>
            <div class="settings-panel-body">
                <div class="settings-group">
                    <label class="settings-label">איכות תצוגה</label>
                    <select id="setting-quality" class="settings-select">
                        <option value="auto">אוטומטי</option>
                        <option value="low">נמוכה</option>
                        <option value="medium">בינונית</option>
                        <option value="high">גבוהה</option>
                        <option value="ultra">מקסימלית</option>
                    </select>
                </div>
                <div class="settings-group">
                    <label class="settings-label">עוצמת שמע</label>
                    <div class="settings-audio-row">
                        <button id="setting-mute" class="settings-mute-btn" title="השתק">🔊</button>
                        <input type="range" id="setting-volume" class="settings-range" min="0" max="100" value="100" />
                        <span id="setting-volume-value" class="settings-range-value">100%</span>
                    </div>
                </div>
                <div class="settings-group">
                    <label class="settings-checkbox-label">
                        <input type="checkbox" id="setting-fit" checked />
                        התאם לחלון
                    </label>
                </div>
                <div class="settings-group">
                    <label class="settings-checkbox-label">
                        <input type="checkbox" id="setting-clipboard" />
                        סנכרון לוח הדבקה
                    </label>
                </div>
                <div class="settings-group">
                    <label class="settings-checkbox-label">
                        <input type="checkbox" id="setting-audio" checked />
                        אפשר שמע
                    </label>
                </div>
            </div>
        `;

        this.container.querySelector('.toolbar')?.appendChild(panel);

        // Close button
        panel.querySelector('.settings-close-btn')?.addEventListener('click', () => this.hideSettingsPanel());

        // Quality change
        panel.querySelector('#setting-quality')?.addEventListener('change', (e) => {
            const quality = (e.target as HTMLSelectElement).value as any;
            this.events.onSettingsChange?.({ quality });
        });

        // Volume slider
        const volumeSlider = panel.querySelector('#setting-volume') as HTMLInputElement;
        const volumeValue = panel.querySelector('#setting-volume-value') as HTMLElement;
        const muteBtn = panel.querySelector('#setting-mute') as HTMLButtonElement;

        volumeSlider?.addEventListener('input', () => {
            const vol = parseInt(volumeSlider.value);
            volumeValue.textContent = `${vol}%`;
            muteBtn.textContent = vol === 0 ? '🔇' : vol < 50 ? '🔉' : '🔊';
            this.onVolumeChange?.(vol / 100);
        });

        muteBtn?.addEventListener('click', () => {
            const isMuted = muteBtn.textContent === '🔇';
            if (isMuted) {
                volumeSlider.value = '100';
                volumeValue.textContent = '100%';
                muteBtn.textContent = '🔊';
                this.onVolumeChange?.(1.0);
                this.onMuteToggle?.(false); // unmuted
            } else {
                volumeSlider.value = '0';
                volumeValue.textContent = '0%';
                muteBtn.textContent = '🔇';
                this.onVolumeChange?.(0);
                this.onMuteToggle?.(true); // muted
            }
        });

        // Checkboxes
        panel.querySelector('#setting-fit')?.addEventListener('change', (e) => {
            this.events.onSettingsChange?.({ fitToWindow: (e.target as HTMLInputElement).checked });
        });
        panel.querySelector('#setting-clipboard')?.addEventListener('change', (e) => {
            this.events.onSettingsChange?.({ enableClipboard: (e.target as HTMLInputElement).checked });
        });
        panel.querySelector('#setting-audio')?.addEventListener('change', (e) => {
            this.events.onSettingsChange?.({ enableAudio: (e.target as HTMLInputElement).checked });
        });

        // Close on click outside
        setTimeout(() => {
            document.addEventListener('click', this.handleOutsideClick);
        }, 10);
    }

    private handleOutsideClick = (e: MouseEvent): void => {
        const panel = this.container.querySelector('.settings-panel');
        const settingsBtn = this.container.querySelector('#settings-btn');
        if (panel && !panel.contains(e.target as Node) && !settingsBtn?.contains(e.target as Node)) {
            this.hideSettingsPanel();
        }
    };

    private hideSettingsPanel(): void {
        this.settingsPanelVisible = false;
        this.container.querySelector('.settings-panel')?.remove();
        document.removeEventListener('click', this.handleOutsideClick);
    }

    // Audio volume callback
    public onVolumeChange?: (volume: number) => void;
    public onMuteToggle?: (muted: boolean) => void;
}
